import React, { useState } from 'react';
import { X, AlertTriangle, Heart, CheckCircle } from 'lucide-react';
import { recordSubscriptionEvent } from '../lib/api/feedback';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import LoadingSpinner from './LoadingSpinner';

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

export default function SubscriptionModal({ isOpen, onClose, onComplete }: SubscriptionModalProps) {
  const { user } = useAuth();
  const { success, error: showError } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [feedback, setFeedback] = useState('');
  const [wantsReengagement, setWantsReengagement] = useState<boolean | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFirstStepSubmit = () => {
    if (feedback.trim()) {
      setCurrentStep(2);
    }
  };

  const handleFinalSubmit = async () => {
    if (!user || wantsReengagement === null) return;

    setIsSubmitting(true);

    try {
      const { error } = await recordSubscriptionEvent(
        user.id,
        'cancellation_request',
        feedback.trim(),
        wantsReengagement
      );

      if (error) throw error;

      setCurrentStep(3); // Show success step
    } catch (err) {
      showError('Failed to Process', 'Please try again or contact support.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetModal = () => {
    setCurrentStep(1);
    setFeedback('');
    setWantsReengagement(null);
    setIsSubmitting(false);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const handleComplete = () => {
    success(
      'Feedback Received',
      wantsReengagement 
        ? 'Thank you for your feedback. We\'ll reach out in 3 months to see if you need goal support!'
        : 'Thank you for your feedback. We\'re sorry to see you go and will use your input to improve.'
    );
    resetModal();
    onComplete();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="subscription-modal-title"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 id="subscription-modal-title" className="text-xl font-semibold text-gray-900">
            {currentStep === 1 ? 'Cancel Subscription' : 
             currentStep === 2 ? 'Stay Connected?' : 
             'Thank You'}
          </h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400"
            aria-label="Close modal"
          >
            <X className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {currentStep === 1 ? (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="h-8 w-8 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  We're sorry to see you go
                </h3>
                <p className="text-gray-600">
                  Please tell us how we could have been better or what we could have done to keep you.
                </p>
              </div>

              <div>
                <label htmlFor="cancellation-feedback" className="block text-sm font-medium text-gray-700 mb-2">
                  Your feedback (required)
                </label>
                <textarea
                  id="cancellation-feedback"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Help us understand what went wrong or what features you needed..."
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-400 focus:border-transparent resize-none"
                  rows={4}
                  maxLength={300}
                  required
                />
                <div className="flex justify-between items-center mt-2">
                  <p className="text-sm text-gray-500">
                    Your honest feedback helps us improve for future users.
                  </p>
                  <span className={`text-sm ${feedback.length > 250 ? 'text-amber-600' : 'text-gray-500'}`}>
                    {feedback.length}/300
                  </span>
                </div>
              </div>

              {/* Quick feedback options */}
              <div className="border-t border-gray-100 pt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Common reasons:</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    'Too expensive',
                    'Not enough features',
                    'Didn\'t help with goals',
                    'Too many messages',
                    'Found alternative',
                    'No longer needed'
                  ].map((reason) => (
                    <button
                      key={reason}
                      type="button"
                      onClick={() => {
                        if (!feedback.includes(reason)) {
                          setFeedback(prev => prev ? `${prev}. ${reason}` : reason);
                        }
                      }}
                      className="px-3 py-2 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 text-left"
                    >
                      {reason}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : currentStep === 2 ? (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="h-8 w-8 text-pink-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  One last question
                </h3>
                <p className="text-gray-600">
                  Would you like us to nudge you in 3 months to check if you need goal help?
                </p>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => setWantsReengagement(true)}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    wantsReengagement === true
                      ? 'border-pink-400 bg-pink-50'
                      : 'border-gray-200 hover:border-pink-300'
                  }`}
                >
                  <div className="font-medium text-gray-900">Yes, remind me in 3 months</div>
                  <div className="text-sm text-gray-600">
                    We'll send a gentle check-in to see if you'd like to restart your accountability journey.
                  </div>
                </button>

                <button
                  onClick={() => setWantsReengagement(false)}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    wantsReengagement === false
                      ? 'border-pink-400 bg-pink-50'
                      : 'border-gray-200 hover:border-pink-300'
                  }`}
                >
                  <div className="font-medium text-gray-900">No, please don't contact me</div>
                  <div className="text-sm text-gray-600">
                    We'll process your cancellation and won't reach out again.
                  </div>
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center space-y-6">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="h-10 w-10 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Thank You for Your Feedback
                </h3>
                <p className="text-gray-600">
                  {wantsReengagement 
                    ? 'We\'ll reach out in 3 months to see if you need goal support again.'
                    : 'We\'re sorry to see you go and will use your feedback to improve OneWin.Today.'
                  }
                </p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800 text-sm">
                  Your subscription will remain active until the end of your current billing period.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex space-x-3">
          {currentStep < 3 ? (
            <>
              <button
                onClick={handleClose}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
              >
                Cancel
              </button>
              
              {currentStep === 1 ? (
                <button
                  onClick={handleFirstStepSubmit}
                  disabled={!feedback.trim()}
                  className="flex-1 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={handleFinalSubmit}
                  disabled={wantsReengagement === null || isSubmitting}
                  className="flex-1 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
                >
                  {isSubmitting ? (
                    <LoadingSpinner size="sm" color="gray" />
                  ) : (
                    <span>Complete Cancellation</span>
                  )}
                </button>
              )}
            </>
          ) : (
            <button
              onClick={handleComplete}
              className="w-full px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}